import React, { useMemo, useState } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { TimeBucketFilterBounds } from './canvasChartData';
import { formatSlotRelativeLabel } from '../utils/timelineUtils';

dayjs.extend(isoWeek);

export type DailyMetricPoint = { xValue: string; metricValue: number | null };

export type ChartContributionHeatmapProps = {
	series: DailyMetricPoint[];
	/** Time filter bounds on the x-axis; the grid shows only days inside them. */
	range?: TimeBucketFilterBounds | null;
	/** Limit columns (ISO weeks) for compact previews. Omit for full range. */
	maxWeeks?: number;
	cellGap?: number;
	cellMinSize?: number;
	/** Fixed grid height as a CSS length; cells stay square, so the grid's width follows from it. Omit to fill the available width. */
	height?: string;
	/** Caps the grid height as a CSS length; below the cap the grid fills the available width. */
	maxHeight?: string;
	/** `tiles`: separate rounded cells. `grid`: cells share thin borders, like a table. */
	appearance?: 'tiles' | 'grid';
	/** Labels each column with its week, e.g. "W2 of Sep", with the relative date as a tooltip. */
	showWeekLabels?: boolean;
};

const MIN_WEEKS = 8;

const isCurrentYear = (date: dayjs.Dayjs) => date.isSame(dayjs(), 'year');

/** E.g. "W2 of Sep": which week of the month the week's Monday falls in, and that month. */
function formatWeekLabel(weekStart: dayjs.Dayjs): string {
	return `W${Math.ceil(weekStart.date() / 7)} of ${weekStart.format('MMM')}`;
}

function formatCellDate(dayKey: string): string {
	const day = dayjs(dayKey);
	return day.format(isCurrentYear(day) ? 'MMM D' : 'MMM D, YYYY');
}

function formatCellTooltip(dayKey: string, value: number): string {
	const date = formatCellDate(dayKey);
	return value ? `${value} ${value === 1 ? 'record' : 'records'} on ${date}` : date;
}

function parseDayKey(raw: string): dayjs.Dayjs | null {
	const key = raw.trim().slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
		return null;
	}
	const d = dayjs(key);
	return d.isValid() ? d.startOf('day') : null;
}

/**
 * GitHub-style activity grid: each column is an ISO week, rows are Mon–Sun.
 */
const ChartContributionHeatmap: React.FC<ChartContributionHeatmapProps> = ({
	series,
	range,
	maxWeeks,
	cellGap = 2,
	cellMinSize = 3,
	height,
	maxHeight,
	appearance = 'tiles',
	showWeekLabels = false,
}) => {
	const theme = useTheme();

	const { weekStarts, cells, maxVal } = useMemo(() => {
		const byDay = new Map<string, number>();
		let dataMin: dayjs.Dayjs | null = null;
		let dataMax: dayjs.Dayjs | null = null;
		for (const p of series) {
			if (p.metricValue === null) {
				continue;
			}
			const d = parseDayKey(p.xValue);
			if (!d) {
				continue;
			}
			const k = d.format('YYYY-MM-DD');
			byDay.set(k, (byDay.get(k) ?? 0) + p.metricValue);
			if (!dataMin || d.isBefore(dataMin)) {
				dataMin = d;
			}
			if (!dataMax || d.isAfter(dataMax)) {
				dataMax = d;
			}
		}

		const today = dayjs().startOf('day');
		const filterStart = range?.start ? parseDayKey(range.start) : null;
		const filterEnd = range?.end ? parseDayKey(range.end) : null;
		const rangeEnd = filterEnd ?? (dataMax && dataMax.isAfter(today) ? dataMax : today);
		let rangeStart = (filterStart ?? dataMin ?? rangeEnd).startOf('isoWeek');
		if (maxWeeks != null && maxWeeks > 0) {
			const fromWeeks = rangeEnd
				.startOf('isoWeek')
				.subtract(maxWeeks - 1, 'week');
			if (fromWeeks.isAfter(rangeStart)) {
				rangeStart = fromWeeks;
			}
		}
		const minWeeksStart = rangeEnd.startOf('isoWeek').subtract(MIN_WEEKS - 1, 'week');
		if (minWeeksStart.isBefore(rangeStart)) {
			rangeStart = minWeeksStart;
		}

		const weekStarts: dayjs.Dayjs[] = [];
		let weekCursor = rangeStart.clone().startOf('isoWeek');
		const rangeEndWeek = rangeEnd.endOf('isoWeek');
		while (!weekCursor.isAfter(rangeEndWeek)) {
			weekStarts.push(weekCursor.clone());
			weekCursor = weekCursor.add(1, 'week');
		}

		const weeksCount = weekStarts.length;
		const cells: { key: string; value: number; weekIdx: number; dow: number; isOutsideRange: boolean }[] = [];
		let maxVal = 0;
		for (let w = 0; w < weeksCount; w++) {
			const monday = weekStarts[w];
			for (let dow = 0; dow < 7; dow++) {
				const d = monday.add(dow, 'day');
				// Without a filter end, omit future days so the ongoing week's column looks in progress.
				if (!filterEnd && d.isAfter(today)) {
					continue;
				}
				const k = d.format('YYYY-MM-DD');
				const isOutsideRange = Boolean((filterStart && d.isBefore(filterStart)) || (filterEnd && d.isAfter(filterEnd)));
				const value = isOutsideRange ? 0 : (byDay.get(k) ?? 0);
				maxVal = Math.max(maxVal, value);
				cells.push({ key: k, value, weekIdx: w, dow, isOutsideRange });
			}
		}

		return { weekStarts, cells, maxVal };
	}, [maxWeeks, range?.end, range?.start, series]);
	const weeks = weekStarts.length;
	const firstCellRow = showWeekLabels ? 2 : 1;
	const [hoveredWeekIdx, setHoveredWeekIdx] = useState<number | null>(null);
	const cellPositions = useMemo(() => new Set(cells.map(({ weekIdx, dow }) => `${weekIdx}:${dow}`)), [cells]);
	const gridLine = (weekIdx: number, dow: number) => {
		const edges = [
			cellPositions.has(`${weekIdx - 1}:${dow}`) && `1px 0 0 0 ${theme.palette.divider} inset`,
			cellPositions.has(`${weekIdx}:${dow - 1}`) && `0 1px 0 0 ${theme.palette.divider} inset`,
		].filter(Boolean);
		return edges.length ? edges.join(', ') : 'none';
	};

	const todayKey = dayjs().format('YYYY-MM-DD');

	const empty = appearance === 'grid' ? 'transparent' : theme.palette.action.hover;

	const cellColor = (v: number) => {
		if (v <= 0 || maxVal <= 0) {
			return empty;
		}
		const t = Math.min(1, v / maxVal);
		return alpha(theme.palette.success.main, 0.22 + 0.72 * t);
	};

	const cellBackground = (key: string, value: number, isOutsideRange: boolean) => {
		if (isOutsideRange) {
			return theme.palette.action.disabledBackground;
		}
		return key === todayKey ? alpha(theme.palette.primary.main, 0.2) : cellColor(value);
	};

	if (weeks === 0) {
		return null;
	}

	const widthForHeight = (h: string) => `calc((${h} - ${6 * cellGap}px) * ${weeks} / 7 + ${(weeks - 1) * cellGap}px)`;
	let width = '100%';
	if (height) {
		width = widthForHeight(height);
	} else if (maxHeight) {
		width = `min(100%, ${widthForHeight(maxHeight)})`;
	}

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateRows: showWeekLabels ? 'auto repeat(7, auto)' : 'repeat(7, auto)',
				gridTemplateColumns: `repeat(${weeks}, minmax(${cellMinSize}px, 1fr))`,
				gap: `${cellGap}px`,
				width,
				mx: 'auto',
			}}
			role="img"
			aria-label="Activity by day"
			onMouseLeave={showWeekLabels ? () => setHoveredWeekIdx(null) : undefined}
		>
			{showWeekLabels && weekStarts.map((weekStart, weekIdx) => (
				<Typography
					key={weekStart.format('YYYY-MM-DD')}
					variant="caption"
					color="text.secondary"
					// Top padding reserves the line the hovered column's relative date is drawn into.
					sx={{ gridColumn: weekIdx + 1, gridRow: 1, alignSelf: 'end', position: 'relative', textAlign: 'center', lineHeight: 1.2, pt: 2, pb: 0.5 }}
				>
					{hoveredWeekIdx === weekIdx && (
						<Box
							component="span"
							sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', color: 'primary.main' }}
						>
							{weekStart.isSame(dayjs(), 'isoWeek') ? 'This week' : formatSlotRelativeLabel(weekStart, dayjs())}
						</Box>
					)}
					{formatWeekLabel(weekStart)}
					{!isCurrentYear(weekStart) && <Box component="span" sx={{ display: 'block' }}>{weekStart.format('YYYY')}</Box>}
				</Typography>
			))}
			{cells.map(({ key, value, weekIdx, dow, isOutsideRange }) => (
				<Tooltip key={`${key}-${weekIdx}-${dow}`} title={isOutsideRange ? `${formatCellDate(key)} · outside filter` : formatCellTooltip(key, value)} enterDelay={400} disableInteractive>
					<Box
						onMouseEnter={showWeekLabels ? () => setHoveredWeekIdx(weekIdx) : undefined}
						sx={{
							gridColumn: weekIdx + 1,
							gridRow: dow + firstCellRow,
							aspectRatio: '1',
							...(appearance === 'grid' && {
								position: 'relative',
								'&::after': {
									content: '""',
									position: 'absolute',
									inset: 0,
									backgroundColor: alpha(theme.palette.text.primary, 0.08),
									opacity: 0,
									transition: 'opacity 0.4s ease-out',
									pointerEvents: 'none',
								},
								'&:hover::after': { opacity: 1, transition: 'none' },
							}),
							backgroundColor: cellBackground(key, value, isOutsideRange),
							...(appearance === 'grid'
								? {
									// Lines are drawn only between two present cells, so the grid has no outer frame, including around missing days.
									boxShadow: gridLine(weekIdx, dow),
								}
								: {
									borderRadius: 0.5,
									border: `1px solid ${theme.palette.divider}`,
								}),
							...(key === todayKey && appearance === 'grid' && { display: 'flex', alignItems: 'center', justifyContent: 'center' }),
						}}
					>
						{key === todayKey && appearance === 'grid' && (
							<Typography variant="caption" color="primary" sx={{ fontSize: '0.625rem' }}>Today</Typography>
						)}
					</Box>
				</Tooltip>
			))}
		</Box>
	);
};

export default ChartContributionHeatmap;
